import { flexRender, Header, Table } from '@tanstack/react-table'
import { cx } from 'class-variance-authority'
import React from 'react'

import { ArrowDown, ArrowUp, ArrowUpDown } from '../icons'

export type TableHeadItemProps<TData> = {
  table: Table<TData>
  header: Header<TData, unknown>
}

export function TableHeadItem<TData>({
  table,
  header,
}: TableHeadItemProps<TData>) {
  if (header.isPlaceholder) {
    return null
  }

  const { rows } = table.getRowModel()

  /**
   * A function to get the target sort icon for a column. tanstack table sorting is
   * represented with either a string of 'asc' or 'desc' or a false value.
   */
  const getSortIcon = (canSort: boolean, currentSorting: string) => {
    if (!canSort) {
      return null
    }

    const icon =
      {
        asc: ArrowUp,
        desc: ArrowDown,
      }[currentSorting] ?? ArrowUpDown

    return icon
  }

  const isActionColumn = header.column.columnDef.meta?.isActionColumn ?? false
  const canResize = header.column.getCanResize() && !isActionColumn
  const canSort = header.column.getCanSort()
  const isResizing = header.column.getIsResizing()
  const SortIcon = getSortIcon(canSort, header.column.getIsSorted() as string)

  return (
    <>
      <div
        className={cx('flex w-full items-center justify-between', {
          'cursor-pointer select-none':
            header.column.getCanSort() && !isActionColumn,
        })}
        onClick={
          isResizing || isActionColumn
            ? undefined
            : header.column.getToggleSortingHandler()
        }
      >
        {flexRender(header.column.columnDef.header, header.getContext())}
        {!isActionColumn && SortIcon && (
          <button
            className="flex h-8 w-8 items-center justify-center focus:outline-none focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-focus-outline"
            disabled={rows.length === 0}
          >
            <SortIcon className="h-4 w-4" strokeWidth={0.5} />
          </button>
        )}
      </div>

      {canResize && (
        <div
          data-testid="resizeHandle"
          onMouseDown={header.getResizeHandler()}
          onTouchStart={header.getResizeHandler()}
          className={cx(
            'absolute right-0 top-0 z-50 h-full w-0.5 cursor-col-resize touch-none bg-opacity-50 opacity-0 transition-opacity duration-75 hover:opacity-100',
            {
              'bg-primary opacity-100': isResizing,
            },
            {
              'bg-background-overlay-hovered': !isResizing,
            },
          )}
        />
      )}
    </>
  )
}
